import React from 'react'
import { motion } from 'framer-motion'
import { Shield, Lock, Eye, Wifi } from 'lucide-react'

const LoadingScreen = () => {
  const iconVariants = {
    initial: { scale: 0, rotate: 0 },
    animate: { 
      scale: [0, 1.2, 1],
      rotate: [0, 180, 360],
      transition: {
        duration: 2,
        repeat: Infinity,
        repeatType: "loop",
        ease: "easeInOut"
      }
    }
  }

  const textVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: {
        delay: 0.5,
        duration: 0.8
      }
    }
  }

  const progressVariants = {
    initial: { width: 0 },
    animate: {
      width: "100%",
      transition: {
        duration: 2,
        ease: "easeInOut"
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyber-900 via-gov-800 to-cyber-900 flex items-center justify-center relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-cyber-400 rounded-full opacity-20"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [-20, -100],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="text-center z-10">
        {/* Logo and Icons */}
        <div className="flex justify-center items-center space-x-4 mb-8">
          <motion.div
            variants={iconVariants}
            initial="initial"
            animate="animate"
            className="text-cyber-400"
          >
            <Shield size={48} />
          </motion.div>
          <motion.div
            variants={iconVariants}
            initial="initial"
            animate="animate"
            style={{ animationDelay: '0.2s' }}
            className="text-success-400"
          >
            <Lock size={40} />
          </motion.div>
          <motion.div
            variants={iconVariants}
            initial="initial"
            animate="animate"
            style={{ animationDelay: '0.4s' }}
            className="text-warning-400"
          >
            <Eye size={36} />
          </motion.div>
          <motion.div
            variants={iconVariants}
            initial="initial"
            animate="animate"
            style={{ animationDelay: '0.6s' }}
            className="text-cyber-300"
          >
            <Wifi size={32} />
          </motion.div>
        </div>

        {/* App Title */}
        <motion.div
          variants={textVariants}
          initial="initial"
          animate="animate"
          className="mb-6"
        >
          <h1 className="text-4xl md:text-6xl font-display font-bold text-white mb-2 text-shadow">
            CyberGov
            <span className="text-cyber-400"> Check</span>
          </h1>
          <p className="text-cyber-200 text-lg md:text-xl font-medium">
            Cybersecurity Assessment for Philippine Government
          </p>
        </motion.div>

        {/* Loading Progress */}
        <motion.div
          variants={textVariants}
          initial="initial"
          animate="animate"
          className="w-64 mx-auto"
        >
          <div className="bg-gov-700 rounded-full h-2 mb-4 overflow-hidden">
            <motion.div
              variants={progressVariants}
              initial="initial"
              animate="animate"
              className="h-full bg-gradient-to-r from-cyber-400 to-cyber-600 rounded-full"
            />
          </div>
          <p className="text-cyber-300 text-sm font-medium">
            Initializing security protocols...
          </p>
        </motion.div>

        {/* Philippine Flag Colors Accent */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 1 }}
          className="flex justify-center space-x-2 mt-8"
        >
          <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
          <div className="w-3 h-3 bg-red-600 rounded-full"></div>
          <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
        </motion.div>
      </div>

      {/* Bottom Text */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center"
      >
        <p className="text-cyber-300 text-sm">
          Securing the Digital Philippines
        </p>
        <p className="text-gov-400 text-xs mt-1">
          Powered by DICT & Local Government Units
        </p>
      </motion.div>
    </div>
  )
}

export default LoadingScreen